import React, { useEffect, useMemo, useState } from 'react'
import { H3 } from '../../../layouts/TextStyles'
import { Box, LinearProgress, MenuItem, Select, Tab, Tabs } from '@mui/material'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { examAnalysisApiSlice, ExamBoardStudent } from '../../../features/curator/examAnalysisSlice'
import { CourseExamState, ExamTypeState } from '../../../features/types'
import CircularProgressWithLabel from '../../../components/muiComponents/circularProgressWithLabel'
import { capitalize } from '../../../utils/functions'
import { tone, ui } from '../../../utils/uiTokens'
import MentorExamComparison from './components/MentorExamComparison'
import ExamBoardTable, { pointOf } from './components/ExamBoardTable'
import { useHeadScope } from '../../head/headScope'
import DirectorSubjectFilter from '../../director/DirectorSubjectFilter'

// Аттас емтихандар бір қойынды: «DOSTYQ online» мен бос орындар елемейді
const examKey = (title: string) => title.replace(/dostyq\s*online/gi, '').replace(/\s+/g, '').toLowerCase()
const examLabel = (title: string) => title.replace(/dostyq\s*online/gi, '').trim() || title

export const RANGES = [
    { from: 0, to: 19, tone: tone.bad },
    { from: 20, to: 29, tone: tone.warn },
    { from: 30, to: 39, tone: tone.yellow },
    { from: 40, to: 50, tone: tone.ok },
]

interface ExamSet {
    key: string
    label: string
    ids: number[]
}

const AnalysisPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const { readOnly } = useHeadScope()
    const { data: listOfExams } = examAnalysisApiSlice.useFetchListOfExamsQuery()
    const [examType, setExamType] = useState<ExamTypeState>((searchParams.get('type') as ExamTypeState) || 'BS')
    const [setKey, setSetKey] = useState('')
    const [subjectCode, setSubjectCode] = useState('')
    const [groupId, setGroupId] = useState(0)
    const [range, setRange] = useState<number | null>(null)
    const [onlyMissed, setOnlyMissed] = useState(false)

    const sets: ExamSet[] = useMemo(() => {
        const out = new Map<string, ExamSet>()
        ;(listOfExams ?? [])
            .filter((e: CourseExamState) => (e.type || 'BS') === examType)
            .forEach((e: CourseExamState) => {
                const title = (e as any)[`title${L}`] || e.titleKz || e.titleRu || ''
                const k = examKey(title)
                const cur = out.get(k)
                if (cur) cur.ids.push(e.id)
                else out.set(k, { key: k, label: examLabel(title), ids: [e.id] })
            })
        return Array.from(out.values())
    }, [listOfExams, examType, L])

    useEffect(() => {
        if (sets.length && !sets.some((s) => s.key === setKey)) setSetKey(sets[0].key)
        if (!sets.length) setSetKey('')
    }, [sets])

    useEffect(() => {
        setSearchParams({ type: examType })
    }, [examType])

    const current = sets.find((s) => s.key === setKey)
    const { data: board, isFetching } = examAnalysisApiSlice.useFetchExamBoardQuery(
        { examIds: current?.ids ?? [] },
        { skip: !current },
    )

    useEffect(() => {
        setGroupId(0)
        setRange(null)
        setOnlyMissed(false)
    }, [setKey])

    const subjects = board?.curatorSubjects ?? []
    useEffect(() => {
        if (subjects.length && !subjects.some((s) => s.code === subjectCode)) setSubjectCode(subjects[0].code)
    }, [subjects])
    const subject = subjects.find((s) => s.code === subjectCode)
    const subjectName = subject ? (subject as any)[`name${L}`] || subject.nameKz : ''

    // топтар выбранного пәна: оқушылар саны и жазды ма
    const groups = useMemo(() => {
        const passed = new Set((board?.students ?? []).filter((s) => s.passed).map((s) => s.userId))
        const out = new Map<number, { id: number; title: string; users: Set<number>; wrote: boolean }>()
        ;(board?.memberships ?? [])
            .filter((m) => m.subjectCode === subjectCode)
            .forEach((m) => {
                const g = out.get(m.groupId) ?? { id: m.groupId, title: m.groupTitle, users: new Set<number>(), wrote: false }
                g.users.add(m.userId)
                if (passed.has(m.userId)) g.wrote = true
                out.set(m.groupId, g)
            })
        return Array.from(out.values()).sort((a, b) => a.title.localeCompare(b.title))
    }, [board, subjectCode])

    const groupOf = useMemo(() => {
        const out = new Map<number, string>()
        groups.forEach((g) => g.users.forEach((u) => out.set(u, g.title)))
        return out
    }, [groups])

    const inScope: ExamBoardStudent[] = useMemo(() => {
        const users = new Set<number>()
        groups.filter((g) => !groupId || g.id === groupId).forEach((g) => g.users.forEach((u) => users.add(u)))
        return (board?.students ?? []).filter((s) => users.has(s.userId))
    }, [board, groups, groupId])

    const passed = inScope.filter((s) => s.passed)
    const missed = inScope.length - passed.length
    const subjectPoints = passed.map((s) => pointOf(s, subjectCode)).filter((p): p is number => p !== null)
    const avg = (xs: number[]) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : 0)
    const rangeCount = (i: number) => subjectPoints.filter((p) => p >= RANGES[i].from && p <= RANGES[i].to).length

    const rows = inScope.filter((s) => {
        if (onlyMissed) return !s.passed
        if (range === null) return true
        const p = pointOf(s, subjectCode)
        return s.passed && p !== null && p >= RANGES[range].from && p <= RANGES[range].to
    })

    const wroteGroups = groups.filter((g) => g.wrote).length
    const pct = inScope.length ? Math.round((passed.length * 100) / inScope.length) : 0

    return (
        <Wrapper>
            <Content>
                <Title>
                    <H3>{formatMessage({ id: 'examAnalysis' })}</H3>
                </Title>
                <DirectorSubjectFilter />
                <Tabs sx={styles.tabs} variant="scrollable" scrollButtons="auto">
                    {(['BS', 'PKS'] as ExamTypeState[]).map((type) => (
                        <Tab key={type} onClick={() => setExamType(type)} sx={styles.typeButton(examType === type)}
                            label={formatMessage({ id: type === 'PKS' ? 'examTypePks' : 'examTypeBs' })} />
                    ))}
                </Tabs>
                <Tabs sx={styles.tabs} variant="scrollable" scrollButtons="auto">
                    {sets.map((s) => (
                        <Tab key={s.key} onClick={() => setSetKey(s.key)} sx={styles.button(setKey === s.key)}
                            label={setKey === s.key && board && !isFetching && groups.length
                                ? `${s.label} · ${formatMessage({ id: 'ebGroupsCount' }, { n: wroteGroups })}`
                                : s.label} />
                    ))}
                </Tabs>

                {isFetching || !board ? (
                    current ? <LinearProgress /> : null
                ) : (
                    <>
                        <Box sx={styles.filters}>
                            {subjects.length > 1 && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Box sx={{ fontSize: '16px', color: ui.sub }}>{formatMessage({ id: 'ebMentorSubject' })}</Box>
                                    {subjects.map((s) => (
                                        <Box key={s.code} component="button" type="button" onClick={() => { setSubjectCode(s.code); setGroupId(0); setRange(null) }}
                                            sx={styles.pill(s.code === subjectCode)}>
                                            {(s as any)[`name${L}`] || s.nameKz}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Select size="small" value={groupId} onChange={(e) => { setGroupId(Number(e.target.value)); setRange(null) }}
                                sx={styles.select}>
                                <MenuItem value={0} sx={{ fontSize: '16px' }}>{formatMessage({ id: 'ebAllGroups' })}</MenuItem>
                                {groups.map((g) => (
                                    <MenuItem key={g.id} value={g.id} sx={{ fontSize: '16px', color: g.wrote ? ui.ink : ui.muted }}>
                                        {g.title} · {formatMessage({ id: 'ebStudentsN' }, { n: g.users.size })}
                                        {!g.wrote && ` · ${formatMessage({ id: 'ebNotWritten' })}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box sx={styles.cards}>
                            <Card>
                                <CircularProgressWithLabel value={pct} size={56} color="secondary" variant="determinate" />
                                <Box>
                                    <CardLabel>{formatMessage({ id: 'ebPassed' })}</CardLabel>
                                    <CardValue>{passed.length} / {inScope.length}</CardValue>
                                </Box>
                            </Card>
                            <Box component="button" type="button" onClick={() => { setOnlyMissed(!onlyMissed); setRange(null) }}
                                sx={styles.range(tone.neutral, onlyMissed)}>
                                <Box>
                                    <CardLabel>{formatMessage({ id: 'ebNotPassed' })}</CardLabel>
                                    <CardValue style={{ color: ui.sub }}>{missed}</CardValue>
                                </Box>
                            </Box>
                            <Card>
                                <Box>
                                    <CardLabel>{formatMessage({ id: 'ebSubjectAvg' }, { subject: subjectName })}</CardLabel>
                                    <CardValue style={{ color: ui.pink }}>{avg(subjectPoints)}</CardValue>
                                </Box>
                            </Card>
                            <Card>
                                <Box>
                                    <CardLabel>{formatMessage({ id: 'ebAvgTotal' })}</CardLabel>
                                    <CardValue>{avg(passed.map((s) => s.totalScore))}</CardValue>
                                </Box>
                            </Card>
                        </Box>

                        <Box sx={styles.cards}>
                            {RANGES.map((r, i) => (
                                <Box key={r.from} component="button" type="button"
                                    onClick={() => { setRange(range === i ? null : i); setOnlyMissed(false) }}
                                    sx={styles.range(r.tone, range === i)}>
                                    <Box sx={{ fontSize: '16px', fontWeight: 600, color: ui.ink }}>
                                        {formatMessage({ id: 'ebRange' }, { range: `${r.from}–${r.to}` })}
                                    </Box>
                                    <Box sx={{ fontSize: '28px', fontWeight: 700, color: r.tone.text }}>{rangeCount(i)}</Box>
                                </Box>
                            ))}
                        </Box>

                        {(range !== null || onlyMissed) && (
                            <Box component="button" type="button" onClick={() => { setRange(null); setOnlyMissed(false) }} sx={styles.clear}>
                                × {formatMessage({ id: 'ebClearFilter' })}
                            </Box>
                        )}

                        {readOnly && current && <MentorExamComparison examId={current.ids[0]} />}

                        <ExamBoardTable rows={rows} subjectCode={subjectCode} subjectName={subjectName} groupOf={groupOf} />
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default AnalysisPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
    @media (max-width: 600px) {
        padding: 16px;
    }
`

const Card = styled.div`
    border-radius: 14px;
    padding: 16px;
    border: 1px solid ${ui.border};
    background: ${ui.card};
    display: flex;
    gap: 14px;
    align-items: center;
    font: inherit;
`

const CardLabel = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${ui.sub};
    margin-bottom: 4px;
`

const CardValue = styled.div`
    font-size: 30px;
    font-weight: 700;
    color: ${ui.ink};
    line-height: 1.1;
`

const styles = {
    tabs: {
        mb: 2,
        display: 'flex',
        minHeight: '40px',
        '& .MuiTabs-indicator': { display: 'none' },
    },
    filters: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mb: 2,
    },
    select: {
        alignSelf: 'flex-start',
        minWidth: 240,
        maxWidth: '100%',
        fontSize: '16px',
        borderRadius: ui.radius,
        backgroundColor: ui.card,
    },
    cards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 2,
        mb: 2,
    },
    range: (t: { text: string; bg: string; border: string }, active: boolean) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        padding: '14px 16px',
        borderRadius: '14px',
        cursor: 'pointer',
        font: 'inherit',
        textAlign: 'left',
        backgroundColor: t.bg,
        border: `2px solid ${active ? t.text : t.border}`,
    }),
    clear: {
        mb: 2,
        border: `1px solid ${ui.border}`,
        background: ui.card,
        color: ui.sub,
        borderRadius: ui.radius,
        padding: '6px 12px',
        fontSize: '16px',
        cursor: 'pointer',
    },
    pill: (active: boolean) => ({
        padding: '8px 14px',
        borderRadius: ui.radius,
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: active ? 600 : 400,
        color: active ? ui.pink : ui.sub,
        backgroundColor: active ? ui.pinkSoft : 'transparent',
        border: `1px solid ${active ? 'transparent' : ui.border}`,
    }),
    typeButton: (selected: boolean) => ({
        borderRadius: '12px',
        minHeight: '40px',
        padding: '0 18px',
        marginRight: '8px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 600,
        backgroundColor: selected ? ui.pink : 'transparent',
        border: `1px solid ${selected ? ui.pink : ui.border}`,
        color: selected ? '#fff' : ui.sub,
        '&.Mui-selected': { color: selected ? '#fff' : ui.sub },
        '&:hover': { borderColor: ui.pink, color: selected ? '#fff' : ui.pink },
    }),
    button: (selected: boolean) => ({
        borderRadius: '12px',
        minHeight: '40px',
        padding: '0 14px',
        marginRight: '8px',
        maxWidth: '360px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: selected ? 600 : 400,
        backgroundColor: selected ? ui.pinkSoft : 'transparent',
        border: `1px solid ${selected ? 'transparent' : ui.border}`,
        color: selected ? ui.pink : ui.sub,
        '&.Mui-selected': { color: ui.pink },
        '&:hover': { borderColor: ui.pink, color: ui.pink },
    }),
}
